import React, { useEffect, useState } from 'react';
import { ActivityIndicator, FlatList, TextInput, View } from "react-native";
import { Text, TouchableOpacity } from '../../../globalComponents/NativeOverrides';
import { useDispatch, useSelector } from 'react-redux';
import { event_getGolfDetailsByTournamentId, event_getTournamentByTournamentId, event_loadExternalPrices } from '../../../store/actionsv1/events';
import Colors from '../../../constants/Colorsv2';
import { AthleteProps, GolfCourseProps, GolfLeaderProps, LoadPriceRequestType, TournamentProps } from '../../../store/types/sr_svc';
import GolfLeaderCard from './GolfLeaderCard';
import { MarketProps } from '../../../store/types/mk_svc';
import { market_getLatestTradesByEventId, market_getOrderStatsByEvent } from '../../../store/actionsv1/markets';
import moment from 'moment';
import GolfTournamentStatsCard from './GolfTournamentStatsCard';
import { Icon } from 'react-native-elements';
import { PublicPlayerProps } from '../../../store/types/auth_svc';

type GolfWidgetProps = {
    tournament_id:string,
    width:number
}

const MAX_WIDTH = 475
const MIN_WIDTH = 400
const options = [ 'Leaderboard', 'Course Stats' ]

const GolfWidget = ({ tournament_id, width }: GolfWidgetProps ) => {
    
    const [ loading, setLoading ] = useState(false);
    const [ active_view, setActiveView ] = useState('summary');
    const [ active_option, setActiveOption ] = useState('Leaderboard')
    const [ toggle_width, setToggleWidth ] = useState(0);
    const [ search_visible, setSearchVisible ] = useState(false);
    const [ market_select_visible, setMarketSelectVisible ] = useState(false);
    const [ search_value, setSearchValue ] = useState('');
    const [ all_leaders, setAllLeaders ] = useState(true);
    const [ active_market_id, setActiveMarketId ] = useState<string|undefined>(undefined)

    const dispatch = useDispatch();
    const player:PublicPlayerProps | undefined = useSelector((state:any) => state.player.player, (left, right) => left?.player_id == right?.player_id)
    const tournament:TournamentProps | undefined = useSelector((state:any) => state.event.tournaments[tournament_id], (left, right) => left?.time_detail == right?.time_detail)
    const markets:MarketProps[] = useSelector((state:any) => state.market.markets, (left, right) => left.length == right.length)
    const athletes:AthleteProps[] = useSelector((state:any) => state.event.athletes_list.athletes, (left, right) => left.length == right.length);
    const active_market = markets.find(m => m.market_id == active_market_id);
    const golf_courses_list:{ updated:number, golf_courses:GolfCourseProps[] } = useSelector((state:any) => state.event.golf_courses_list, (left, right) => left.updated == right.updated);
    const golf_course = golf_courses_list.golf_courses.find(gc => gc.tournament_id == tournament_id)

    let view_width = width > MAX_WIDTH ? MAX_WIDTH : width
    if(view_width < MIN_WIDTH){ view_width = MIN_WIDTH }

    //Get golf leaders and sort them based on the status of the tournament
    const golf_leaders_list:{ updated:number, golf_leaders:GolfLeaderProps[] } = useSelector((state:any) => state.event.golf_leaders_list, (left, right) => left.updated == right.updated);
    let golf_leaders = golf_leaders_list.golf_leaders.filter(tl => tl.tournament_id == tournament_id).sort((a,b) => a.rank - b.rank)
    
    let started = golf_leaders.find(l => l.place > 1) ? true : false
    if(started){
        golf_leaders = golf_leaders.sort((a,b) => a.place - b.place)
    }
    if(search_value != ''){
        let filtered_athletes = athletes.filter(a => `${a.first_name} ${a.last_name}`.toLowerCase().includes(search_value.toLowerCase()))
        golf_leaders = golf_leaders.filter(gl => filtered_athletes.map(a => a.athlete_id.toString()).includes(gl.athlete_id.toString()))
    } else if(!all_leaders){
        golf_leaders = golf_leaders.filter((tl, index) => index <= 3)
    }
    //----------


    useEffect(() => {
        getTournamentFromServer(tournament_id)
    },[tournament_id])

    useEffect(() => {
        if(!tournament){ return }
        setActiveMarketId(tournament.supported_markets[0]?.market_id)
    },[tournament])

    useEffect(() => {
        if(!active_market){ return }
        dispatch(market_getOrderStatsByEvent(tournament_id, 'tournament', active_market.market_id))
    },[active_market])

    const getTournamentFromServer = async(tournament_id:string) => {
        setLoading(true);
        await dispatch(event_getTournamentByTournamentId(tournament_id))
        await dispatch(event_getGolfDetailsByTournamentId(tournament_id))
        await dispatch(market_getLatestTradesByEventId(tournament_id, 'tournament'))
        setLoading(false);
    }

    const handleLoadPrices = async() => {
        if(!tournament || !player || player.role !== 'admin'){ return }
        if(!active_market){ return }
        if(!tournament.supported_markets || tournament.supported_markets.length === 0){ return alert('There are no supported markets available for tournament') }
        if(!active_market){ return alert('No market') }
        let price_request:LoadPriceRequestType = {
            event_id: tournament_id,
            event_type: 'tournament',
            level: active_market.level as 'event' | 'athlete',
            market_ids: [ active_market.market_id ],
            activate: true
        }
        await dispatch(event_loadExternalPrices(price_request))
    }

    const renderOptions = (data: { item:string, index:number }) => {
        const selected = data.item == active_option ? true : false
        return (
            <TouchableOpacity style={{ width: (toggle_width/2) - 2, backgroundColor:selected?Colors.shades.white:'transparent', padding:10, borderRadius:22 }} onPress={() => setActiveOption(data.item)}>
                <Text textAlign='center' size={14} color={Colors.brand.midnight} weight={selected?'bold':'regular'}>{data.item}</Text>
            </TouchableOpacity>
        )
    }

    const renderTournamentLeaders = (data:{ item:GolfLeaderProps, index:number }) => {
        if(!golf_course || !tournament){ return <></> }
        return (
            <View style={{ margin:4 }}>
                <GolfLeaderCard 
                    golf_course={golf_course}
                    market={active_market}
                    tournament={tournament}
                    width={view_width}
                    golf_leader={data.item}
                />
            </View>
        )
    }

    if(!tournament || loading){
        return (
            <View style={{ padding:20 }}>
                <ActivityIndicator style={{ alignSelf: 'center' }} size='large' color={Colors.brand.midnight} />
            </View>
        )
    }

    const supported_markets = markets.filter(m => tournament.supported_markets?.map(sm => sm.market_id.toString()).includes(m.market_id))
    return (
        <View style={{ flex:1, width:view_width }}>
            <View nativeID='tournament_header' style={{ padding:10, borderBottomWidth:1, borderBottomColor:Colors.shades.shade600 }}>
                <Text size={24} color={Colors.brand.midnight} weight='bold'>{tournament.tournament_name}</Text>
                <View style={{ flexDirection:'row', alignItems:'center' }}>
                    <View style={{ flex:1 }}>
                    <Text style={{ marginTop:5 }} size={16} color={Colors.brand.midnight} weight='light'>{moment(tournament.scheduled_datetime).format('MMM DD YYYY')}</Text>
                        {golf_course ?
                        <View style={{ marginTop:5 }}>
                            <Text size={14} color={Colors.brand.slate} weight='light'>Par {golf_course.par} - Yards {golf_course.yardage}</Text>
                        </View>
                        :<></>}
                    </View>
                    {player?.role == 'admin' ?
                    <TouchableOpacity style={{ borderRadius:22, borderColor:Colors.brand.electric, padding:10, borderWidth:1 }} onPress={() => handleLoadPrices()}>
                        <Text size={14} color={Colors.brand.electric} weight='semibold' textAlign='center'>Update Prices</Text>
                    </TouchableOpacity>
                    :<></>}
                </View>
            </View>
            {active_view == 'summary' ?
            <View nativeID='summary' style={{ padding:10}}>
                <View nativeID='view_selector' style={{ margin:15, borderRadius:22, padding:2, backgroundColor:Colors.shades.shade300 }} onLayout={(ev) => {
                    const { width } = ev.nativeEvent.layout;
                    setToggleWidth(width)
                }}>
                    <FlatList
                        data={options}
                        renderItem={renderOptions}
                        horizontal
                        showsHorizontalScrollIndicator={false}
                    />
                </View>
                {active_option == 'Leaderboard' ?
                <View nativeID='tournament_leaderboard'>
                    <View nativeID='leaderboard_header' style={{ padding:5, flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
                        <View style={{ flexDirection:'row', padding:4, alignItems:'center', borderRadius:22, backgroundColor:Colors.shades.shade100 }}>
                            <TouchableOpacity style={{ padding:10 }} onPress={() => {
                                if(search_visible){ setSearchValue('') }
                                setSearchVisible(!search_visible)
                            }}>
                                <Icon color={search_visible?Colors.utility.error:Colors.brand.electric} name={search_visible?'x-circle':'search'} size={18} type='feather'/>
                            </TouchableOpacity>
                            {search_visible ?
                            <TextInput
                                style={{ flex:1, padding:10, borderRadius:22, backgroundColor:Colors.shades.white }}
                                placeholder='Search Golfer'
                                value={search_value}
                                placeholderTextColor={Colors.brand.slate}
                                onChangeText={(text) => setSearchValue(text)}
                            />
                            :<></>}
                        </View>
                        {active_market ?
                        <View>
                            <TouchableOpacity style={{ flexDirection:'row', alignItems:'center', padding:10, borderRadius:22, borderColor:Colors.brand.electric, borderWidth:1 }} onPress={() => setMarketSelectVisible(!market_select_visible)}>
                                <Text size={14} color={Colors.brand.electric} weight='regular'>{active_market.type}</Text>
                                <Icon name='chevron-down' type='feather' size={14} color={Colors.brand.electric} />
                            </TouchableOpacity>
                            {market_select_visible ?
                            <View style={{ position:'absolute', bottom:0, padding:5, right:0, borderRadius:8, backgroundColor:Colors.shades.white, minWidth:100, ...local_styles.raised }}>
                                {supported_markets.map(sm => {
                                    let market = markets.find(m => m.market_id == sm.market_id)
                                    if(!market){ return <></> }
                                    return (
                                        <TouchableOpacity style={{ padding:10, borderBottomWidth:1, borderBottomColor:Colors.shades.shade600 }} onPress={() => { 
                                            setActiveMarketId(sm.market_id)
                                            setMarketSelectVisible(false)
                                        }}>
                                            <Text size={14} color={Colors.brand.electric} weight='regular'>{market.type}</Text>
                                        </TouchableOpacity>
                                    )
                                })}
                            </View>
                            :<></>}
                        </View>
                        :<></>}
                    </View>
                    <View nativeID='tournament_leaderboard_headers' style={{ padding:5, flexDirection:'row', alignItems:'center' }}>
                        <Text style={{ flex:1, margin:2 }} size={14} color={Colors.brand.midnight} weight='bold'>POS</Text>
                        <Text style={{ flex:5, margin:2 }} size={14} color={Colors.brand.midnight} weight='bold'>{tournament.participant_type=='athlete'?'Player':'Team'}</Text>
                        <Text style={{ flex:1, margin:2 }} size={14} color={Colors.brand.midnight} weight='bold'>SCORE</Text>
                    </View>
                    <FlatList
                        data={golf_leaders}
                        renderItem={renderTournamentLeaders}
                    />
                </View>
                :active_option == 'Course Stats' ?
                <View nativeID='tournament_statistics'>
                    <GolfTournamentStatsCard
                        tournament_id={tournament_id}
                    />
                </View>
                :<></>}
            </View>
            :<></>}
        </View>
    )
}

const local_styles = {
    raised: { shadowColor: "rgba(0, 0, 0, 0.12)", shadowOffset: { width: 0, height: 10 }, shadowRadius: 10, shadowOpacity: 1 }
}

const areEqual = (left:GolfWidgetProps, right:GolfWidgetProps) => {
    if(left.tournament_id != right.tournament_id){ return false }
    if(left.width != right.width){ return false }
    return true
}
export default React.memo(GolfWidget, areEqual)